import React, {forwardRef, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState} from "react";
import classnames from "classnames";
import LazyImage from "../../../../../../../../components/app-specific/lazy-image";
import Slider from "react-slick";
import {UserHomepageComponentTypes, UserHomepageControlPanelEvents, UserHomepageEventEmitter} from "../../../../../../models";
import {UserHomepageControllerContext, UserHomepageViewContext} from "../../../../../../index";
import {ReactComponent as Prev} from "../../../../../../../../../assets/images/web-homepage/slide-show-prev.svg";
import {ReactComponent as Next} from "../../../../../../../../../assets/images/web-homepage/slide-show-next.svg";
import ObserversService from "../../../../../../../../../core/services/observers";
import {bannerWidthToHeightRatio} from "../../../../../../../../../core/constants/enums";
import debounce from "lodash/debounce";
import useIsMounted from "../../../../../../../../hooks/use-is-mounted";


const UserWebHomepageSlideShow = forwardRef(({component, tabId}, ref) => {
    const controller = useContext(UserHomepageControllerContext);
    const {zoom} = useContext(UserHomepageViewContext);
    const [activeIndex, setActiveIndex] = useState(0);
    const [bannerHeight, setBannerHeight] = useState(187);
    /**@type {React.MutableRefObject<import('@types/react-slick').Slider>}*/
    const slickSliderRef = useRef();
    /**@type {React.MutableRefObject<HTMLDivElement>}*/
    const containerRef = useRef();
    const dragging = useRef(false);
    const isMounted = useIsMounted();

    const banners = useMemo(() => component?.content?.banners ?? [], [component]);

    /**
     * Exposes the slick api ref functionalities to the parent component.
     */
    useImperativeHandle(ref, () => slickSliderRef.current, [slickSliderRef])

    /**
     * With each change in the controller:
     * - attaches the event listeners for content selection for focusing purposes.
     */
    useEffect(() => {
        controller.on(UserHomepageControlPanelEvents.onContentSelected, focusComponentContent);
        return () => {
            controller.off(UserHomepageControlPanelEvents.onContentSelected, focusComponentContent);
        }
    }, [component, banners, controller])

    /**
     * With each change in the zoom value of the view context:
     * - attaches an observer to recompute the height of the banners and to adjust the size of the banner
     */
    useEffect(() => {
        if (!containerRef.current)
            return;
        const throttledCallback = debounce((entries, observer) => adjustBannerSize(entries, observer), 100);
        const observer = ObserversService.newResizeObserver(throttledCallback);
        ObserversService.observeResizeObserver(observer, containerRef.current);
        throttledCallback([containerRef.current]);
        return () => ObserversService.disconnectResizeObserver(observer);
    }, [containerRef, zoom])

    /**
     * Recomputes the height of the banners and to adjust the size of the banner.
     * @param {ResizeObserverEntry[] | Element[]} entries
     */
    const adjustBannerSize = (entries) => {
        requestAnimationFrame(() => {
            if (!isMounted())
                return;
            const entry = entries[0];
            const width = entry?.contentRect?.width ?? entry?.getBoundingClientRect()?.width ?? 0;
            setBannerHeight((width - 10) / bannerWidthToHeightRatio);
        })
    }

    /**
     * Uses the slick slider api to bring the selected content into view.
     *
     * @param {number} tabId
     * @param {number} componentId
     * @param {number} contentId
     */
    const focusComponentContent = (tabId, componentId, contentId) => {
        // only componentId is checked since each component has a unique id in the server.
        if (componentId !== component.id || component.type?.id !== UserHomepageComponentTypes.slideShow)
            return;
        const contentIndex = banners?.findIndex(e => e.id === contentId);
        if (contentIndex === -1)
            return;
        slickSliderRef.current.slickGoTo(contentIndex);
    }


    /**
     * Invokes the appropriate callback of the controller for the click of the banner.
     * @param banner
     */
    const onBannerClicked = (banner) => {
        if (dragging.current)
            return;
        controller.clickContent(tabId, component.id, banner.id, UserHomepageEventEmitter.view);
    }

    /**
     * Invokes the appropriate callback of the controller for the selection of the banner.
     * @param banner
     */
    const onBannerSelected = (banner) => {
        controller.selectContent(tabId, component.id, banner.id);
    }

    /**
     * Sets the dragging flag of the slideshow so that while dragging, the clicks of the banners would have no effect.
     */
    const beforeSlideChange = () => {
        dragging.current = true;
    }

    /**
     * sets the current active index in the state and invokes the appropriate callback of the controller for the
     * selection of the banner
     * @param index
     */
    const afterSlideChange = (index) => {
        setActiveIndex(index);
        dragging.current = false
        onBannerSelected(banners[index]);
    }


    /**
     * Changes the carousel item based on the offset.
     *
     * * ff the offset is greater than zero shifts it the right, otherwise, shifts it to the left
     * @param {number} offset
     */
    const changeSlide = (offset) => {
        if (offset > 0) {
            slickSliderRef.current.slickNext();
            return;
        }
        slickSliderRef.current.slickPrev();
    }

    /**
     * Changes the carousel based on the selected index of the dots.
     *
     * * if the difference of index is positive navigates slick carouse forward otherwise, navigates slick backwards
     * @param {number} index
     */
    const onDotsClicked = (index) => {
        if (index === activeIndex)
            return;
        slickSliderRef.current.slickGoTo(index);
    }

    return (
        <>
            <div className={'invisible'}/>
            <div ref={containerRef} className={classnames('user-web-homepage-slideshow')}>
                <div>
                    {
                        banners?.length > 1 &&
                        <button
                            className={'slider-icon-button prev'}
                            onClick={() => changeSlide(-1)}>
                            <Prev/>
                        </button>
                    }
                    <Slider
                        infinite
                        draggable
                        initialSlide={activeIndex}
                        ref={slickSliderRef}
                        slidesToShow={1}
                        slidesToScroll={1}
                        arrows={false}
                        speed={250}
                        beforeChange={beforeSlideChange}
                        afterChange={afterSlideChange}
                    >
                        {banners?.map((banner) => (
                            <div
                                onClick={() => onBannerClicked(banner)}
                                key={banner.id}
                                id={banner.webViewElementId}
                                className={'image'}
                            >
                                <LazyImage
                                    src={banner?.imageName}
                                    alt={banner?.imageName ?? ''}
                                    height={bannerHeight}
                                />
                            </div>
                        ))}
                    </Slider>
                    {
                        banners?.length > 1 &&
                        <button
                            className={'slider-icon-button next'}
                            onClick={() => changeSlide(1)}>
                            <Next/>
                        </button>
                    }
                    <div className={'dots'}>
                        {banners?.map((banner, index) => (
                            <div
                                key={banner.id}
                                className={classnames(
                                    'dot',
                                    {
                                        'active': index === activeIndex,
                                    }
                                )}
                                onClick={() => onDotsClicked(index)}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
})

export default UserWebHomepageSlideShow;
